/**
 * IftExploreIndia — landing to the wider Indias.com network + Indian
 * state deep-links. Preserves the "Explore India" nav slot on
 * IndianFamilyTree.com so visitors get a single hub for state pages.
 *
 * Route: /explore-india
 */
import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink, MapPin, Compass } from "lucide-react";
import SeoHead from "@/components/SeoHead";

const P = {
  bg: "#FDF9EF", ink: "#3A1F0F", inkSoft: "#6B4A2E",
  saffron: "#B25D38", saffronSoft: "#F7D6C1",
  green: "#0E7C55", greenSoft: "#C6EAD6",
  gold: "#C08A2E", goldSoft: "#F5E6C4",
};

const NORTH = ["delhi","haryana","punjab","himachal-pradesh","jammu-and-kashmir","ladakh","uttar-pradesh","uttarakhand","rajasthan"];
const WEST  = ["gujarat","maharashtra","goa","madhya-pradesh","chhattisgarh"];
const SOUTH = ["kerala","tamil-nadu","karnataka","andhra-pradesh","telangana","puducherry"];
const EAST  = ["west-bengal","odisha","bihar","jharkhand"];
const NE    = ["assam","meghalaya","tripura","manipur","mizoram","nagaland","arunachal-pradesh","sikkim"];

const REGIONS = [
  { name: "North India", key: "north", slugs: NORTH },
  { name: "West India",  key: "west",  slugs: WEST },
  { name: "South India", key: "south", slugs: SOUTH },
  { name: "East India",  key: "east",  slugs: EAST },
  { name: "Northeast",   key: "ne",    slugs: NE },
];

const NETWORK_LINKS = [
  { name: "Indias.com",     href: "https://indias.com",     tagline: "The India-wide destination portal" },
  { name: "Kerala.com",     href: "https://kerala.com",     tagline: "Flagship state portal — travel, culture, community" },
  { name: "NaturePulse",    href: "https://naturepulse.cloud",tagline: "Environment & wildlife pulse of the subcontinent" },
  { name: "MyFamilyTree.care", href: "https://myfamilytree.care", tagline: "The heritage archive for global Indian families" },
  { name: "Social Pulse",   href: "https://socialpulse.com",tagline: "The parent network — communities, causes, civic pulse" },
];

function stateLabel(slug) {
  return slug.split("-").map(w => w[0].toUpperCase() + w.slice(1)).join(" ");
}

export default function IftExploreIndia() {
  useEffect(() => { document.title = "Explore India | IndianFamilyTree.com"; }, []);
  return (
    <div style={{ background: P.bg, color: P.ink }} data-testid="ift-explore-india">
      <SeoHead
        section="genealogy"
        title="Explore India — 28 States, One Living Archive"
        description="Deep-link into every Indian state page, plus the broader Indias network of sister portals — Indias.com, Kerala.com, NaturePulse, MyFamilyTree.care and Social Pulse."
      />

      {/* Hero */}
      <section className="border-b" style={{ borderColor: P.saffronSoft, background: `linear-gradient(180deg, ${P.saffronSoft} 0%, ${P.bg} 100%)` }}>
        <div className="max-w-5xl mx-auto px-4 py-14">
          <div className="text-[10px] font-mono uppercase tracking-wider" style={{ color: P.saffron }}>
            <Compass className="inline w-3 h-3 mr-1" /> A living map
          </div>
          <h1 className="font-display font-bold leading-tight text-4xl sm:text-5xl lg:text-6xl mt-2 mb-4">Explore India — one country, many worlds.</h1>
          <p className="text-base sm:text-lg" style={{ color: P.inkSoft }}>
            Every Indian state carries its own languages, cuisines, festivals and family lineages. Tap a state below to visit its dedicated archive page, or step across to the wider Indias network for travel, nature, community and news.
          </p>
        </div>
      </section>

      {/* Indias network */}
      <section className="max-w-5xl mx-auto px-4 py-10">
        <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>The Indias network</div>
        <h2 className="font-display text-2xl md:text-3xl mb-4">Sibling sites across the network.</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          {NETWORK_LINKS.map((n) => (
            <a key={n.name} href={n.href} target="_blank" rel="noopener noreferrer"
               className="flex items-start gap-3 rounded-2xl border p-4 bg-white hover:shadow-md transition"
               style={{ borderColor: P.saffronSoft }}
               data-testid={`ift-explore-network-${n.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: P.saffronSoft }}>
                <ExternalLink className="w-5 h-5" style={{ color: P.saffron }} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="font-display text-base">{n.name}</div>
                <div className="text-xs mt-0.5" style={{ color: P.inkSoft }}>{n.tagline}</div>
              </div>
              <ArrowRight className="w-4 h-4 opacity-40" />
            </a>
          ))}
        </div>
      </section>

      {/* State grid */}
      {REGIONS.map((r) => (
        <section key={r.key} className="max-w-5xl mx-auto px-4 py-6" data-testid={`ift-explore-region-${r.key}`}>
          <div className="text-[10px] font-mono uppercase tracking-wider mb-1" style={{ color: P.saffron }}>
            <MapPin className="inline w-3 h-3 mr-1" /> {r.name}
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
            {r.slugs.map((slug) => (
              <Link key={slug} to={`/state/${slug}`}
                    className="rounded-xl border px-3 py-2.5 bg-white hover:shadow-sm transition flex items-center justify-between gap-2"
                    style={{ borderColor: P.saffronSoft }}
                    data-testid={`ift-explore-state-${slug}`}>
                <span className="font-display text-sm">{stateLabel(slug)}</span>
                <ArrowRight className="w-3 h-3 opacity-40" style={{ color: P.saffron }} />
              </Link>
            ))}
          </div>
        </section>
      ))}

      {/* Closing CTA */}
      <section className="max-w-5xl mx-auto px-4 pt-6 pb-16">
        <div className="rounded-3xl border p-8 md:p-10 text-center" style={{ borderColor: P.saffronSoft, background: `linear-gradient(135deg, ${P.saffronSoft} 0%, ${P.bg} 55%, ${P.goldSoft} 100%)` }}>
          <h3 className="font-display text-2xl md:text-3xl mb-3">Every family comes from somewhere.</h3>
          <p className="text-sm md:text-base max-w-2xl mx-auto mb-5" style={{ color: P.inkSoft }}>
            Ready to map your ancestral village onto the wider network? Start with your family tree — we'll help you tie it to a state, a district and (eventually) a specific village heritage page.
          </p>
          <Link to="/family-trees/new" className="inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white shadow-sm" style={{ background: P.saffron }} data-testid="ift-explore-cta">
            Start your family tree <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
